import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Lock, Download, Video, Globe } from "lucide-react";
import { clientApi, fileUrlFor, formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";
import Logo from "@/components/Logo";

const inputCls = "field-input";

const STATUS_CLS = {
  pending: "bg-amber-100 text-amber-800", confirmed: "bg-emerald-100 text-emerald-800",
  completed: "bg-sky-100 text-sky-800", cancelled: "bg-red-100 text-red-700", "no-show": "bg-slate-200 text-slate-600",
  unpaid: "bg-amber-100 text-amber-800", paid: "bg-emerald-100 text-emerald-800", refunded: "bg-slate-200 text-slate-600",
};

const Badge = ({ value }) => (
  <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${STATUS_CLS[value] || "bg-slate-100 text-slate-600"}`}>{value}</span>
);

function Login({ onSuccess }) {
  const { t, lang, setLang } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await clientApi.post("/client-auth/login", { email, password });
      localStorage.setItem("stratedge-client-token", data.token);
      onSuccess(data.token);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-navy-950 px-4" data-testid="client-login-page">
      <button
        type="button"
        data-testid="client-language-toggle"
        onClick={() => setLang(lang === "en" ? "ar" : "en")}
        className="absolute end-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:border-gold"
      >
        <Globe className="h-3.5 w-3.5" /> {lang === "en" ? "العربية" : "EN"}
      </button>
      <div className="w-full max-w-md rounded-lg bg-white p-10 shadow-2xl">
        <Logo />
        <h1 className="mt-8 font-display text-3xl font-semibold text-navy">{t("portal.clientTitle")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("portal.clientSub")}</p>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="client-login-form">
          <div>
            <label htmlFor="client-email" className="field-label">{t("form.email")}</label>
            <input id="client-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="client-email-input" autoComplete="username" />
          </div>
          <div>
            <label htmlFor="client-password" className="field-label">{t("portal.password")}</label>
            <input id="client-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="client-password-input" autoComplete="current-password" />
          </div>
          {error && <p className="rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert" data-testid="client-login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="client-login-submit"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-navy py-3.5 text-sm font-bold text-white hover:bg-navy-800 disabled:opacity-60">
            <Lock className="h-4 w-4" /> {loading ? t("portal.signingIn") : t("portal.signin")}
          </button>
        </form>
        <p className="mt-5 text-center text-xs text-slate-400">
          <Link to="/" className="font-semibold text-gold-hover hover:underline">{t("portal.backToSite")}</Link>
          {" · "}{t("portal.support")} <a href="mailto:info@stratedgeuk.com" className="font-semibold text-gold-hover">info@stratedgeuk.com</a>
        </p>
      </div>
    </div>
  );
}

function BookingCard({ b }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid={`client-booking-${b.booking_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-xs font-bold text-slate-400">{b.booking_id}</p>
          <h3 className="mt-1 font-display text-xl font-semibold text-navy">{b.expert_name}</h3>
          <p className="text-sm text-slate-500 capitalize">{b.consultation_type || "expert"} consultation — {b.topic}</p>
        </div>
        <div className="flex gap-2">
          <Badge value={b.booking_status} />
          <Badge value={b.payment_status} />
        </div>
      </div>
      <div className="mt-4 grid gap-2 text-sm text-slate-600 sm:grid-cols-3">
        <p>{b.date} · {b.time} ({b.timezone})</p>
        <p>{b.duration} min</p>
        <p className="font-bold text-navy">USD {Number(b.total_amount).toLocaleString()}</p>
      </div>
      {b.meeting_link && (
        <a href={b.meeting_link} target="_blank" rel="noopener noreferrer" data-testid={`client-meeting-link-${b.booking_id}`}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-xs font-bold text-white hover:bg-navy-800">
          <Video className="h-4 w-4" /> Join Meeting
        </a>
      )}
      {b.files?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-3">
          {b.files.map((f, i) => (
            <a key={i} href={fileUrlFor(f.path, "client")} data-testid={`client-file-${b.booking_id}-${i}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold-hover hover:underline">
              <Download className="h-3.5 w-3.5" /> {f.filename}
            </a>
          ))}
        </div>
      )}
      <div className="mt-4 border-t border-slate-100 pt-4">
        <Link to={`/experts/${b.expert_id}/book`} data-testid={`client-book-again-${b.booking_id}`}
          className="inline-flex items-center gap-2 rounded-full border border-gold px-5 py-2 text-xs font-bold text-gold-hover hover:bg-gold-soft">
          Book Again / Schedule Follow-up
        </Link>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [me, setMe] = useState(null);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    clientApi.get("/client-portal/me").then((r) => setMe(r.data)).catch(() => onLogout());
    clientApi.get("/client-portal/bookings").then((r) => setBookings(r.data)).catch(() => {});
  }, []);

  if (!me) return <div className="flex min-h-screen items-center justify-center text-sm text-slate-400">Loading...</div>;

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter((b) => b.date >= today && !["cancelled", "completed", "no-show"].includes(b.booking_status));
  const past = bookings.filter((b) => !upcoming.includes(b));

  return (
    <div className="min-h-screen bg-slate-100" data-testid="client-portal-dashboard">
      <header className="flex h-16 items-center justify-between bg-navy-950 px-5">
        <Logo dark compact />
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-300">{me.full_name || me.email}</span>
          <button onClick={onLogout} data-testid="client-logout-button" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs font-bold text-white hover:border-gold">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
        <h1 className="font-display text-3xl font-semibold text-navy">My Consultations</h1>
        <p className="mt-1 text-sm text-slate-500">Track bookings, payments, files and meeting links. Repeat bookings are arranged through Stratedge UK.</p>

        <h2 className="mt-10 font-display text-2xl font-semibold text-navy">Upcoming</h2>
        <div className="mt-4 space-y-4" data-testid="client-upcoming">
          {upcoming.map((b) => <BookingCard key={b.id} b={b} />)}
          {upcoming.length === 0 && <p className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-400">No upcoming consultations.</p>}
        </div>

        <h2 className="mt-10 font-display text-2xl font-semibold text-navy">Past Consultations</h2>
        <div className="mt-4 space-y-4" data-testid="client-past">
          {past.map((b) => <BookingCard key={b.id} b={b} />)}
          {past.length === 0 && <p className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-400">No past consultations.</p>}
        </div>
      </main>
    </div>
  );
}

export default function ClientPortal() {
  const [token, setToken] = useState(localStorage.getItem("stratedge-client-token"));
  if (!token) return <Login onSuccess={(tok) => setToken(tok)} />;
  return <Dashboard onLogout={() => { localStorage.removeItem("stratedge-client-token"); setToken(null); }} />;
}
